import React, { useState } from "react";
import { useCreateTV } from "../hooks/useTVQueries.jsx";
import { tvApi } from "../api/tvApi.jsx";
import TVAuthModal from "./TVAuthModal.jsx";
import useTVStore from "../store/tvStore.jsx";
import { useToast } from "../context/ToastContext.jsx";

const TVManagement = ({
  tvs,
  selectedTVId,
  onTVSelect,
  isLoading,
  isAdminMode = false,
}) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [createMode, setCreateMode] = useState("new"); // 'new' or 'copy'

  const [newTVData, setNewTVData] = useState({
    name: "", // 현황판 ID (백엔드의 name 필드)
    pin: "",
    conferenceTvId: null, // 복사할 현황판 ID
    isBgmEnabled: false, // BGM 활성화 여부
  });

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authTVId, setAuthTVId] = useState(null);
  // 개별 TV 로딩 상태로 변경
  const [loadingTvId, setLoadingTvId] = useState(null);

  // 복사 처리 중 상태 추가
  const [isProcessingCopy, setIsProcessingCopy] = useState(false);

  // TV 인증 상태
  const { clearTvAuth, setCurrentTV } = useTVStore();

  // Toast 훅
  const { toastSuccess, toastError } = useToast();

  const createTVMutation = useCreateTV();

  // 현황판 ID 유효성 검사 함수 (백엔드 패턴과 일치)
  const validateTvName = (name) => {
    // 영어소문자, 숫자, 대시만 허용하되 대시는 가운데에서만 가능
    const pattern = /^[a-z0-9]+(-[a-z0-9]+)*$/;
    return pattern.test(name) && name.length > 0;
  };

  const validatePin = (pin) => {
    const pattern = /^\d{6}$/;
    return pattern.test(pin);
  };

  // 복사할 현황판 데이터 조회 함수 - name으로 조회하도록 수정
  const getCopySourceData = async (tvId) => {
    try {
      // tvs 배열에서 해당 tvId의 name 찾기
      const targetTv = tvs.find((tv) => tv.id === tvId);
      if (!targetTv || !targetTv.name) {
        throw new Error("복사할 현황판 정보를 찾을 수 없습니다.");
      }

      // name으로 조회
      const response = await tvApi.getTvPlay(targetTv.name);

      if (!response.success) {
        throw new Error(response.message || "현황판 데이터 조회 실패");
      }

      const tvData = response.data;

      return {
        contentMappings: tvData.contentMappings || [],
        bgmMappings: tvData.bgmMappings || [],
        isBgmEnabled: tvData.isBgmEnabled || false,
        transitionType: tvData.transitionType || "FADE",
        transitionDuration: tvData.transitionDuration || 1,
        // PIN은 복사하지 않음 (보안상 이유)
        pin: null,
      };
    } catch (error) {
      throw error;
    }
  };

  // 복사된 데이터를 새 현황판에 적용하는 함수
  const applyCopyDataToNewTV = async (newTvId, copyData, newPin) => {
    try {
      // ContentManagement와 MusicManagement의 변환 함수들을 동적으로 import
      const { default: ContentManagement } = await import(
        "./ContentManagement.jsx"
      );
      const { default: MusicManagement } = await import(
        "./MusicManagement.jsx"
      );

      // 컨텐츠 매핑 변환 (기존 ID 제거하여 새로 생성되도록)
      const convertedContentMappings = copyData.contentMappings.map(
        (mapping, index) => {
          const content = mapping.content;

          return {
            mappingId: null, // 새로 생성
            sortOrder: index + 1,
            playDuration: mapping.playDuration || 10,
            name: mapping.name || content?.title || `컨텐츠 ${index + 1}`,
            isMusicPlay: mapping.isMusicPlay || false,
            contentRequest: {
              contentType: content?.contentType || "IMAGE",
              sortOrder: index + 1,
              playDuration: mapping.playDuration || 10,
              isMusicPlay: mapping.isMusicPlay || false,
              // 기존 컨텐츠 참조 (같은 파일/URL 재사용)
              existingContentId: content?.id || null,
              // binaryData와 url은 기존 컨텐츠 참조로 대체
              binaryData: null,
              url: null,
              mimeType: null,
            },
          };
        }
      );

      // 음악 매핑 변환 (기존 ID 제거하여 새로 생성되도록)
      const convertedBgmMappings = copyData.bgmMappings.map(
        (mapping, index) => {
          const bgm = mapping.bgm;

          return {
            mappingId: null, // 새로 생성
            sortOrder: index + 1,
            playDuration: mapping.playDuration || 180,
            volume: mapping.volume || 50,
            name: mapping.name || bgm?.title || `음악 ${index + 1}`,
            bgmRequest: {
              contentType: bgm?.contentType || "FILE",
              sortOrder: index + 1,
              playDuration: mapping.playDuration || 180,
              volume: mapping.volume || 50,
              // 기존 BGM 참조 (같은 파일/URL 재사용)
              existingBgmId: bgm?.id || null,
              // binaryData와 url은 기존 BGM 참조로 대체
              binaryData: null,
              url: null,
            },
          };
        }
      );

      // 백엔드 TvUpdateRequest 스펙에 맞는 데이터 구성
      const updateData = {
        pin: newPin,
        isBgmEnabled: copyData.isBgmEnabled,
        transitionType: copyData.transitionType,
        transitionDuration: copyData.transitionDuration,
        bgmMappings: convertedBgmMappings || [], // @NotNull이므로 빈 배열이라도 전달
        contentMappings: convertedContentMappings || [], // @NotNull이므로 빈 배열이라도 전달
      };

      // tvApi.updateTv 호출
      const updateResponse = await tvApi.updateTv(newTvId, updateData);

      if (!updateResponse.success) {
        throw new Error(updateResponse.message || "복사 데이터 적용 실패");
      }

      return updateResponse.data;
    } catch (error) {
      throw error;
    }
  };

  const handleAddTV = async () => {
    if (!newTVData.name.trim()) {
      toastError("현황판 ID를 입력하세요.");
      return;
    }

    if (!newTVData.pin.trim()) {
      toastError("PIN 번호를 입력하세요.");
      return;
    }

    if (!validatePin(newTVData.pin.trim())) {
      toastError("PIN은 6자리 숫자여야 합니다.");
      return;
    }

    // 백엔드 스펙에 맞는 이름 유효성 검사
    if (!validateTvName(newTVData.name.trim())) {
      toastError(
        "현황판 ID는 영어 소문자, 숫자, 대시(-)만 사용할 수 있습니다.\n대시는 가운데에서만 사용 가능합니다."
      );
      return;
    }

    // 복사 모드에서 복사할 현황판이 선택되지 않은 경우
    if (createMode === "copy" && !newTVData.conferenceTvId) {
      toastError("복사할 현황판을 선택하세요.");
      return;
    }

    const tvName = newTVData.name.trim();
    const tvPin = newTVData.pin.trim();
    setIsProcessingCopy(true);

    try {
      if (createMode === "new") {
        // 새로 만들기 모드 - PIN 포함
        const payload = {
          name: tvName,
          pin: tvPin,
          conferenceTvId: null,
          isBgmEnabled: newTVData.isBgmEnabled || false,
        };

        await createTVMutation.mutateAsync(payload);

        toastSuccess("새 현황판이 성공적으로 생성되었습니다.");
      } else {
        // 복사 모드 - 2단계 처리

        // 1단계: 복사할 현황판 데이터 조회
        const copyData = await getCopySourceData(newTVData.conferenceTvId);

        // 2단계: 새 현황판 생성 (기본 구조만)
        const createPayload = {
          name: tvName,
          pin: tvPin,
          conferenceTvId: null, // 기본 생성만 하고 데이터는 나중에 적용
          isBgmEnabled: copyData.isBgmEnabled || false,
        };

        const newTvId = await createTVMutation.mutateAsync(createPayload);

        if (!newTvId || typeof newTvId !== "number") {
          throw new Error(
            "새 현황판 생성 응답에서 유효한 ID를 찾을 수 없습니다."
          );
        }

        // 3단계: 복사 데이터를 새 현황판에 적용
        await applyCopyDataToNewTV(newTvId, copyData, tvPin);

        toastSuccess(
          `기존 현황판을 복사하여 새 현황판 "${tvName}"이 생성되었습니다.`
        );
      }

      setShowAddModal(false);
      resetModalData();
    } catch (error) {
      // 백엔드 에러 메시지 처리
      let errorMessage = `현황판 ${
        createMode === "copy" ? "복사" : "생성"
      }에 실패했습니다.`;
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toastError(errorMessage);
    } finally {
      setIsProcessingCopy(false);
    }
  };

  // 🎯 수정된 TV 클릭 처리 함수 - 관리자 모드에서는 인증 생략
  const handleTVClick = async (tvId) => {
    if (loadingTvId === tvId) return; // 해당 TV가 이미 로딩 중이면 무시

    try {
      setLoadingTvId(tvId); // 특정 TV 로딩 시작

      // 🎯 관리자 모드에서는 인증 절차 생략하고 바로 TV 데이터 조회
      if (isAdminMode) {
        await loadTVData(tvId);
        return;
      }

      // 🎯 일반 모드에서는 기존 로직 유지 (인증 여부 확인)
      // 1. 인증 여부 확인
      const authResponse = await tvApi.getTvAuthentication(tvId);

      if (!authResponse.success) {
        throw new Error(
          authResponse.message || "인증 여부 확인에 실패했습니다."
        );
      }

      const authRequired = authResponse.data; // true or false

      if (!authRequired) {
        // 2-1. 인증이 필요없는 경우: 바로 TV 데이터 조회
        await loadTVData(tvId);
      } else {
        // 2-2. 인증이 필요한 경우: 인증 모달 표시
        setAuthTVId(tvId);
        setShowAuthModal(true);
      }
    } catch (error) {
      toastError(error.message || "TV 인증 여부 확인에 실패했습니다.");
    } finally {
      setLoadingTvId(null); // 로딩 완료
    }
  };

  const loadTVData = async (tvId) => {
    try {
      // tvs 배열에서 해당 tvId의 name 찾기
      const targetTv = tvs.find((tv) => tv.id === tvId);
      if (!targetTv || !targetTv.name) {
        throw new Error("TV 정보를 찾을 수 없습니다.");
      }

      const tvResponse = await tvApi.getTvPlay(targetTv.name);

      if (!tvResponse.success) {
        throw new Error(tvResponse.message || "TV 데이터 조회에 실패했습니다.");
      }

      // TV 데이터 설정 및 선택
      const tvData = tvResponse.data;
      setCurrentTV(tvData);
      onTVSelect(tvId);

      toastSuccess(`${tvData.name || tvId} 현황판이 선택되었습니다.`);
    } catch (error) {
      toastError(error.message || "TV 데이터 조회에 실패했습니다.");
    }
  };

  // 인증 성공 처리
  const handleAuthSuccess = async (sessionId) => {
    try {
      // 인증 성공 후 TV 데이터 조회
      await loadTVData(authTVId);

      // 모달 닫기
      setShowAuthModal(false);
      setAuthTVId(null);
    } catch (error) {
      // 인증은 성공했지만 TV 데이터 로드 실패 시에도 모달은 닫기
      setShowAuthModal(false);
      setAuthTVId(null);
    }
  };

  // 인증 모달 닫기
  const handleAuthModalClose = () => {
    setShowAuthModal(false);
    setAuthTVId(null);
    clearTvAuth();
  };

  const resetModalData = () => {
    setCreateMode("new");
    setNewTVData({
      name: "",
      pin: "",
      conferenceTvId: null,
      isBgmEnabled: false,
    });
  };

  const closeModal = () => {
    setShowAddModal(false);
    resetModalData();
  };

  // 현황판 ID 입력 필터링 (실시간 유효성 검사)
  const handleNameChange = (e) => {
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "");
    setNewTVData((prev) => ({ ...prev, name: value }));
  };

  const handlePinChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
    setNewTVData((prev) => ({ ...prev, pin: value }));
  };

  if (isLoading) {
    return (
      <div
        style={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          borderRadius: "16px",
          padding: "30px",
          color: "white",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            className="spinner-small"
            style={{
              borderColor: "rgba(255,255,255,0.3)",
              borderTopColor: "white",
            }}
          ></div>
          <span style={{ marginLeft: "10px" }}>현황판 목록 로딩 중...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* 현황판 관리 섹션 */}
      <div
        style={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          borderRadius: "16px",
          padding: "30px",
          color: "white",
          position: "relative",
        }}
      >
        {/* 헤더 */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "25px",
          }}
        >
          <div>
            <h3
              style={{
                margin: "0 0 8px 0",
                fontSize: "18px",
                fontWeight: "600",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              📺 현황판 관리
              {/* 🎯 관리자 모드 표시 */}
              {isAdminMode && (
                <span
                  style={{
                    fontSize: "11px",
                    background: "rgba(255,255,255,0.2)",
                    padding: "2px 6px",
                    borderRadius: "4px",
                    fontWeight: "500",
                  }}
                >
                  관리자 모드
                </span>
              )}
            </h3>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            style={{
              background: "rgba(255,255,255,0.2)",
              color: "white",
              border: "1px solid rgba(255,255,255,0.3)",
              borderRadius: "25px",
              padding: "8px 20px",
              fontSize: "14px",
              fontWeight: "500",
              cursor: "pointer",
              transition: "all 0.2s",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
            onMouseOver={(e) => {
              e.target.style.background = "rgba(255,255,255,0.3)";
              e.target.style.transform = "translateY(-1px)";
            }}
            onMouseOut={(e) => {
              e.target.style.background = "rgba(255,255,255,0.2)";
              e.target.style.transform = "translateY(0)";
            }}
          >
            ✚ 추가
          </button>
        </div>

        {/* TV 목록 */}
        {tvs && tvs.length > 0 ? (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "12px",
              alignItems: "center",
            }}
          >
            {tvs.map((tv) => (
              <button
                key={tv.id}
                onClick={() => handleTVClick(tv.id)}
                disabled={loadingTvId === tv.id} // 해당 TV만 비활성화
                style={{
                  background:
                    selectedTVId === tv.id
                      ? "rgba(255,255,255,0.25)"
                      : "rgba(255,255,255,0.15)",
                  color: "white",
                  border:
                    selectedTVId === tv.id
                      ? "2px solid rgba(255,255,255,0.6)"
                      : "1px solid rgba(255,255,255,0.3)",
                  borderRadius: "25px",
                  padding: "10px 20px",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor: loadingTvId === tv.id ? "wait" : "pointer", // 해당 TV만 wait 커서
                  transition: "all 0.2s",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  minWidth: "fit-content",
                  opacity: loadingTvId === tv.id ? 0.7 : 1, // 해당 TV만 opacity 변경
                }}
                onMouseOver={(e) => {
                  if (selectedTVId !== tv.id && loadingTvId !== tv.id) {
                    // 로딩 중이 아닐 때만 호버 효과
                    e.target.style.background = "rgba(255,255,255,0.2)";
                    e.target.style.transform = "translateY(-1px)";
                  }
                }}
                onMouseOut={(e) => {
                  if (selectedTVId !== tv.id && loadingTvId !== tv.id) {
                    // 로딩 중이 아닐 때만 호버 효과
                    e.target.style.background = "rgba(255,255,255,0.15)";
                    e.target.style.transform = "translateY(0)";
                  }
                }}
              >
                {tv.name || tv.id}
                {/* 해당 TV만 로딩 스피너 표시 */}
                {loadingTvId === tv.id && (
                  <div
                    style={{
                      width: "12px",
                      height: "12px",
                      border: "2px solid rgba(255,255,255,0.3)",
                      borderTop: "2px solid white",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                      marginLeft: "4px",
                    }}
                  />
                )}
              </button>
            ))}
          </div>
        ) : (
          <div
            style={{
              textAlign: "center",
              padding: "20px 0",
              opacity: 0.8,
            }}
          >
            <div style={{ fontSize: "2em", marginBottom: "10px" }}>📺</div>
            <p style={{ margin: "0 0 5px 0", fontSize: "16px" }}>
              등록된 현황판이 없습니다
            </p>
            <p style={{ margin: 0, fontSize: "14px", opacity: 0.7 }}>
              "추가" 버튼을 클릭해서 현황판을 추가해주세요
            </p>
          </div>
        )}
      </div>

      {/* CSS 애니메이션 추가 */}
      <style jsx>{`
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      `}</style>

      {/* 현황판 추가 모달 */}
      {showAddModal && (
        <div className="modal" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">📺 현황판 추가</h2>
              <button className="close-btn" onClick={closeModal}>
                &times;
              </button>
            </div>

            <div className="modal-body">
              {/* 모드 선택 */}
              <div style={{ marginBottom: "25px" }}>
                <div
                  style={{
                    display: "flex",
                    gap: "15px",
                    marginBottom: "15px",
                  }}
                >
                  <button
                    onClick={() => setCreateMode("new")}
                    style={{
                      flex: 1,
                      padding: "12px 20px",
                      border:
                        createMode === "new"
                          ? "2px solid #667eea"
                          : "1px solid #e9ecef",
                      borderRadius: "12px",
                      background: createMode === "new" ? "#f0f4ff" : "#f8f9fa",
                      color: createMode === "new" ? "#667eea" : "#6c757d",
                      cursor: "pointer",
                      transition: "all 0.2s",
                      fontSize: "14px",
                      fontWeight: "500",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                    }}
                  >
                    <span
                      style={{
                        background:
                          createMode === "new" ? "#667eea" : "#6c757d",
                        color: "white",
                        borderRadius: "4px",
                        padding: "2px 6px",
                        fontSize: "10px",
                        fontWeight: "bold",
                      }}
                    >
                      NEW
                    </span>
                    새로 만들기
                  </button>

                  <button
                    onClick={() => setCreateMode("copy")}
                    style={{
                      flex: 1,
                      padding: "12px 20px",
                      border:
                        createMode === "copy"
                          ? "2px solid #667eea"
                          : "1px solid #e9ecef",
                      borderRadius: "12px",
                      background: createMode === "copy" ? "#f0f4ff" : "#f8f9fa",
                      color: createMode === "copy" ? "#667eea" : "#6c757d",
                      cursor: "pointer",
                      transition: "all 0.2s",
                      fontSize: "14px",
                      fontWeight: "500",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                    }}
                  >
                    📺 기존 현황판 복사
                  </button>
                </div>
              </div>

              {/* 복사할 현황판 선택 (복사 모드일 때만) */}
              {createMode === "copy" && (
                <div className="form-group" style={{ marginBottom: "20px" }}>
                  <label className="form-label">복사할 현황판 선택</label>
                  <select
                    className="form-select"
                    value={newTVData.conferenceTvId || ""}
                    onChange={(e) =>
                      setNewTVData((prev) => ({
                        ...prev,
                        conferenceTvId: e.target.value
                          ? parseInt(e.target.value)
                          : null,
                      }))
                    }
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      border: "2px solid #e9ecef",
                      borderRadius: "8px",
                      fontSize: "14px",
                      background: "white",
                    }}
                  >
                    <option value="">선택하세요</option>
                    {tvs &&
                      tvs.map((tv) => (
                        <option key={tv.id} value={tv.id}>
                          {tv.name} (BGM: {tv.bgmCount || 0}개, 컨텐츠:{" "}
                          {tv.contentCount || 0}개)
                        </option>
                      ))}
                  </select>
                  <small style={{ color: "#666", fontSize: "12px" }}>
                    선택한 현황판의 모든 컨텐츠가 복사됩니다.
                  </small>
                </div>
              )}

              {/* 새 현황판 ID */}
              <div className="form-group" style={{ marginBottom: "20px" }}>
                <label className="form-label">새 현황판 ID *</label>
                <input
                  type="text"
                  className="form-input"
                  value={newTVData.name}
                  onChange={handleNameChange}
                  placeholder="현황판 ID 입력 (예: 604, meeting-a, lobby-display)"
                  style={{
                    fontFamily: newTVData.name.match(/^[a-z0-9-]+$/)
                      ? "monospace"
                      : "inherit",
                    width: "100%",
                    padding: "12px 16px",
                    border: "2px solid #e9ecef",
                    borderRadius: "8px",
                    fontSize: "14px",
                    background: "white",
                  }}
                />
                <small style={{ color: "#666", fontSize: "12px" }}>
                  소문자, 숫자, 대시(-)만 사용할 수 있습니다. 대시는
                  가운데에서만 사용 가능합니다.
                </small>
              </div>

              <div className="form-group" style={{ marginBottom: "20px" }}>
                <label className="form-label">PIN 번호 *</label>
                <input
                  type="text"
                  className="form-input"
                  value={newTVData.pin}
                  onChange={handlePinChange}
                  placeholder="6자리 숫자 입력 (예: 123456)"
                  maxLength="6"
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    border: `2px solid ${
                      newTVData.pin && !validatePin(newTVData.pin)
                        ? "#dc3545"
                        : "#e9ecef"
                    }`,
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontFamily: "monospace",
                    background: "white",
                  }}
                />
                <small
                  style={{
                    color:
                      newTVData.pin && !validatePin(newTVData.pin)
                        ? "#dc3545"
                        : "#666",
                    fontSize: "12px",
                  }}
                >
                  {newTVData.pin && !validatePin(newTVData.pin)
                    ? "PIN은 숫자 6자리여야 합니다."
                    : "현황판 접근 시 사용할 6자리 숫자를 입력하세요."}
                </small>
              </div>

              {/* 가이드 */}
              <div
                style={{
                  background: "#e3f2fd",
                  padding: "12px 16px",
                  borderRadius: "8px",
                  border: "1px solid #bbdefb",
                  marginBottom: "25px",
                }}
              >
                <div
                  style={{
                    fontSize: "12px",
                    color: "#1976d2",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    marginBottom: "8px",
                  }}
                >
                  💡 현황판 생성 가이드
                </div>
                <div
                  style={{
                    fontSize: "11px",
                    color: "#1565c0",
                    lineHeight: "1.4",
                    textAlign: "left",
                    paddingLeft: "15px",
                  }}
                >
                  • ID 형식: lobby-display, meeting-room-1, tv-001 등<br />
                  • PIN: 현황판 접근 시 필요한 6자리 숫자
                  <br />
                </div>
                <div
                  style={{
                    marginTop: "8px",
                    fontSize: "11px",
                    color: "#1565c0",
                    borderTop: "1px solid #90caf9",
                    paddingTop: "6px",
                    paddingLeft: "15px",
                    textAlign: "left",
                  }}
                >
                  🔗 생성 후 URL: 현재주소/현황판id
                </div>
              </div>

              <div style={{ marginTop: "25px", textAlign: "right" }}>
                <button
                  className="btn btn-secondary"
                  onClick={closeModal}
                  style={{ marginRight: "10px" }}
                  disabled={isProcessingCopy}
                >
                  취소
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleAddTV}
                  disabled={
                    !newTVData.name.trim() ||
                    !newTVData.pin.trim() ||
                    !validatePin(newTVData.pin) ||
                    createTVMutation.isLoading ||
                    isProcessingCopy ||
                    (createMode === "copy" && !newTVData.conferenceTvId)
                  }
                  style={{
                    background:
                      createMode === "copy"
                        ? "linear-gradient(45deg, #4caf50, #45a049)"
                        : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  }}
                >
                  {isProcessingCopy
                    ? "복사 처리 중..."
                    : createTVMutation.isLoading
                    ? "생성 중..."
                    : createMode === "copy"
                    ? "✚ 현황판 복사"
                    : "✚ 현황판 생성"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 🎯 TV 인증 모달 - 관리자 모드가 아닐 때만 표시 */}
      {!isAdminMode && (
        <TVAuthModal
          isOpen={showAuthModal}
          onClose={handleAuthModalClose}
          tvId={authTVId}
          tvName={tvs.find((tv) => tv.id === authTVId)?.name}
          onAuthSuccess={handleAuthSuccess}
        />
      )}
    </>
  );
};

export default TVManagement;
